import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { Commands, Downloads, Results, Search } from '@local/client-contracts';
import { Constants } from '@local/common';
import { KeyName } from '@local/ts-infra';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService, Shortcuts } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import {
  isChangeFavoriteStatusCommand,
  isDownloadUrlCommand,
  isOpenUrlCommand,
  isPreviewCommand,
  isShareCommand,
  isSummaryCommand,
} from '@shared/utils';
import { getDownloadIcon } from '@shared/utils/set-icon.util';
import { WorkspacesService } from 'src/app/bar/services';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import {
  ContextMenuInvokeSource,
  ResultsResourceItemContextMenuHelper,
} from 'src/app/bar/services/commands/results-resource-item-context-menu-helper';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { PreviewMode } from '../../preview/model/preview-mode';
import { FeedbackPopupDisplayView } from '../../results/components/feedback-popup/feedback-popup.component';
import { InvokeCommand } from '../../results/models/invoke-command.model';
import { ResultContextMenuItem, ResultContextMenuItemId, SearchResults, TelemetryTrigger } from '../../results/models/results-types';
import { ViewItem } from '../../results/models/view-item';
import { ResultCommandBuilder, ResultCommandBuilderParams } from '../../results/services/result-command-builder.service';
import { getPrimaryActionClick, isWikiCard, isWikiItem } from '../../results/utils/results.util';
import { EventResource, ViewItemComponent } from './view-item.component';
import { SearchPopUpService } from '../../special-views/search-popup/search-popup.service';

export type onChangeMode = 'model' | 'selected';

@UntilDestroy()
@Component({
  selector: 'view-item',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class ResourceViewItemComponent extends ViewItemComponent<Search.ResultResourceItem> implements OnInit, ViewItem {
  showIndicateFeedbackSent = false;
  feedbackDisplayView: FeedbackPopupDisplayView;
  private _feedbackSent = false;

  get feedbackSent(): boolean {
    return this._feedbackSent;
  }

  set feedbackSent(value: boolean) {
    this._feedbackSent = value;
  }

  get resourceId() {
    return this.model.resource?.id;
  }

  get resourceAppId() {
    return this.model.resource?.appId;
  }

  get linkId() {
    return this.model.resource?.linkId;
  }

  protected get shareCommandContext(): Commands.Context {
    return {
      resource: { id: this.resourceId, kind: 'link-resource' },
      linkId: this.model.resource?.linkId,
      searchId: this.searchId,
    };
  }

  get displayedSnippet(): string {
    return this.fullSnippet ? this.model.snippet : this.model.snippet.trim().substring(0, 100);
  }

  protected fullSnippet = false;

  @Input() set previewState(val: PreviewMode) {
    this._previewState = val;
  }

  get previewState(): PreviewMode {
    return this._previewState;
  }

  constructor(
    hostRef: ElementRef,
    cdr: ChangeDetectorRef,
    logService: LogService,
    applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    resultCommandBuilder: ResultCommandBuilder,
    protected hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      resultCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      workspacesService,
      searchPopUpService
    );
  }

  protected getEventResource(list = 'search_bar_results'): EventResource {
    if (!this.model.link) {
      return <EventResource>{
        ...super.getEventResource(list),
      };
    }
    return <EventResource>{
      ...super.getEventResource(list),
      appId: this.model.link.appId,
      linkId: this.model.link.id,
    };
  }

  ngOnInit() {
    super.ngOnInit();
    this.buildDragData();
  }

  onChanges(changes: onChangeMode) {
    super.onChanges(changes);
    if (this.model?.snippet) {
      if (!this._selected) this.fullSnippet = false;
    }
    this.restrictedLink = this.model?.link?.shareOptions?.restricted;
    this.buildDragData();
    this.cdr.markForCheck();
  }

  protected async updateThumbnailUrl(): Promise<string> {
    if (!this.model?.view?.thumbnail?.url) {
      this.thumbnail = undefined;
      return;
    }
    if (this.model.view.thumbnail.blobId) {
      const url = await this.blobsService.getBlobFullUrl(this.model.view.thumbnail.blobId);
      this.thumbnail = url;
      return url;
    }
    if (this.model['fileId']) {
      const url = await this.blobsService.getCdnUrl(this.model.view.thumbnail.url);
      this.thumbnail = url;
      return url;
    }
    const request: Downloads.ResourceDownloadRequest = {
      resource: this.model.resource,
      type: 'Resource',
      url: this.model.view.thumbnail.url,
      grantType: 'links:api',
    };
    const url = await this.downloadsService.getDownloadUrl(request);
    this.thumbnail = url;
    return url;
  }

  protected async subscribeOnCommandBarSelect({ item: { command }, via }) {
    if (isShareCommand(command)) {
      this.openSharePopup(via, 'command-bar');
      return;
    } else {
      if (isChangeFavoriteStatusCommand(command)) {
        await this.toggleFavorite(via, 'command_bar');
      } else if (isDownloadUrlCommand(command)) {
        this.download({ command, invokerItem: this.model, trigger: via, eventInfo: { jsonData: { 'action.trigger': 'command_bar' } } });
      } else {
        this.onInvoke({ command, invokerItem: this.model, trigger: via, eventInfo: { jsonData: { 'action.trigger': 'command_bar' } } });
      }
    }
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    const searchModel: SearchResults = this.model as SearchResults;

    const ev: Partial<EventInfo> & { label: ResultContextMenuItemId | 'open_menu' } = {
      label: 'open_menu',
      location: { title: this.getLocation() },
      resources: this.getEventResources(),
    };
    this.sendEvent(ev);
    const itemsToRemove = [];
    if (!this.showResultSections.showFavoriteContextMenuItem) {
      itemsToRemove.push('favorite');
    }
    const disabledCollections = await this.workspacesService.isFeatureFlagDisabled(Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG);

    if (!this.showResultSections.showAddToCollectionContextMenuItem || this.model?.link?.appId === 'pc' || disabledCollections) {
      itemsToRemove.push('add_to_collection');
    }
    if (this.showResultSections.showSummaryAction === false) {
      itemsToRemove.push('summary');
    }
    const ref = await this.contextMenuHelper.open(
      event,
      this.model,
      source,
      'popup',
      null,
      false,
      true,
      true,
      null,
      this.isPreviewClickAction(searchModel?.action) &&
        !this.disablePreviewContextMenu &&
        this.showResultSections.showPreviewIconContextMenu,
      itemsToRemove,
      false
    );
    ref.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });

    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.contextOpen.emit();
  }

  async onInvoke(invokeData: InvokeCommand): Promise<void> {
    if (
      // wiki card - on open
      isOpenUrlCommand(invokeData.command) &&
      getPrimaryActionClick((this.model as SearchResults)?.action) === 'preview' &&
      invokeData?.trigger?.includes('context_menu') &&
      isWikiCard(this.model)
    ) {
      invokeData.command = this.buildPreviewCommand();
    } else if (
      isWikiCard(this.model) &&
      getPrimaryActionClick((this.model as SearchResults)?.action) === 'preview' &&
      (isPreviewCommand(invokeData.command) || isOpenUrlCommand(invokeData.command)) &&
      this.previewState === 'side'
    ) {
      this.invokeSelectedIndex.emit();
      return;
    } else if (
      isOpenUrlCommand(invokeData.command) &&
      !invokeData?.trigger?.includes('context_menu') &&
      getPrimaryActionClick((this.model as SearchResults)?.action) === 'preview' &&
      (['avatar', 'title'].includes(invokeData.source) || this.isClickable)
    ) {
      invokeData.command = this.buildPreviewCommand();
    } else if (isSummaryCommand(invokeData.command) && isWikiItem(this.model) && !this.model.resourceId) {
      this.model.resourceId = await this.getResourceIdByExternalId(this.model.id);
      invokeData.command.resourceId = this.model.resourceId;
    }
    this.invoke.emit({ ...invokeData, invokerItem: this.model });
  }

  protected async buildPresentableCommandsParams(): Promise<ResultCommandBuilderParams> {
    const resultCommandBuilderParams: ResultCommandBuilderParams = await super.buildPresentableCommandsParams();

    const downloadUrl: { url: string } | null = await this.getDownloadUrl();
    return {
      ...resultCommandBuilderParams,
      resourceId: this.resourceId,
      download: downloadUrl,
      preview: this.buildPreviewCommand(),
    };
  }

  private async shouldAllowDownload(): Promise<boolean> {
    const title = this.model?.view?.title as Results.Title;
    return (
      title?.onDrag &&
      isDownloadUrlCommand(title.onDrag) &&
      this.model.link?.appId !== 'pc' &&
      (await this.flagsService.isEnabled('fileDownloads', this.model.link?.id))
    );
  }

  private async getDownloadUrl(): Promise<{ url: string } | null> {
    if (!(await this.shouldAllowDownload())) return null;
    const title = this.model?.view?.title as Results.Title;
    return isDownloadUrlCommand(title.onDrag) ? { url: title.onDrag.url } : null;
  }

  buildDragData() {
    if (!this.model) return;
    const icon = getDownloadIcon(this.model.view?.icon, this.model.view?.iconOverlay);
    this.dragMetadata = {
      type: isWikiCard(this.model) ? 'Url' : 'Resource',
      resource: this.model.resource,
      icon,
      eventInfo: {
        search: {
          clientSearchId: this.clientSearchId,
          sessionId: this.sessionId,
        },
        position: this.index,
        list: this.listName,
      },
    };
    this.dragData = {
      metadata: this.dragMetadata,
      model: this.model.view?.title,
    };
    this.cdr.markForCheck();
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new ResultsResourceItemContextMenuHelper(
      this.resultCommandBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
  }

  async onContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger, source?: string) {
    if (item.command.type === 'change-favorite-status') {
      await this.toggleFavorite(trigger);
    } else if (item.command.type === 'download-url') {
      await this.download({ command: item.command, context: item.data, trigger });
    } else {
      await this.onInvoke({ command: item.command, context: item.data, trigger });
    }
    this.contextClose.emit();

    const reportInteractionClick = trigger.includes('click');

    if (!reportInteractionClick) {
      return;
    }

    const label: string = item.id;

    if (label === 'favorite') {
      return;
    }

    const title = this.getLocation();
    const list = title?.split('/')[0] ? title?.split('/')[0] : null;

    const ev: Partial<EventInfo> = {
      label,
      location: { title },
      resources: this.getEventResources(list),
    };
    this.sendEvent(ev, source === 'preview' ? 'context_menu.open_from_preview' : null);
  }

  protected async setShortcutsHandlers() {
    const { download } = (await this.keyboardService.loadShortcuts<Shortcuts>(['_home'])) ?? {};

    await super.setShortcutsHandlers();

    if (download && (await this.shouldAllowDownload())) {
      this.shortcutsHandlers.push({
        fn: async (item: ResourceViewItemComponent, trigger: TelemetryTrigger, eventInfo) => {
          if (await this.shouldAllowDownload()) item.download({ command: this.prepareDownloadCommand(), trigger, eventInfo });
        },
        keyBinding: download as KeyName[],
      });
    }
  }

  private prepareDownloadCommand() {
    const dragCommand: Commands.DragCommand = (this.model.view.title as Results.Title).onDrag;
    if (!isDownloadUrlCommand(dragCommand)) return;
    const value = dragCommand.url;
    return {
      type: 'download-url',
      value,
    };
  }

  private async download(invokeData?: InvokeCommand) {
    const dragCommand: Commands.DragCommand = (this.model.view.title as Results.Title).onDrag;
    if (!isDownloadUrlCommand(dragCommand)) return;
    const icon = getDownloadIcon(this.model.view.icon, this.model.view.iconOverlay);

    this.eventsService.event('context_menu.download', {
      name: 'action',
      resources: this.getEventResources(),
      location: { title: this.getLocation() },
    });
    if (this.model['fileId']) {
      const req: Downloads.UrlDownloadRequest = {
        type: 'Url',
        url: dragCommand.url,
        grantType: 'blobs',
      };
      await this.downloadsService.urlDownload(req);
    } else {
      const req: Downloads.ResourceDownloadRequest = {
        type: 'Resource',
        url: dragCommand.url,
        resource: this.model.resource,
        icon,
      };
      await this.downloadsService.resourceDownload(req);
    }
  }

  showExtendedSnippet() {
    this.fullSnippet = true;
  }

  private async getResourceIdByExternalId(id: string): Promise<string> {
    const resources = await this.resourcesService.getBulkByExternalId([id], 'cache-first', 'Cloud');
    const resourceId = resources[0]?.resource?.id;
    if (!resourceId) {
      this.logger.warn('could not find resource for external id', { externalId: id });
    }
    return resourceId;
  }

  async onSummary(event?: MouseEvent, resourceId?: string) {
    event.stopPropagation();
    if (isWikiItem(this.model)) {
      if (!this.model.resourceId) {
        this.model.resourceId = await this.getResourceIdByExternalId(this.model?.id);
      }
      resourceId = this.model.resourceId;
    }
    return super.onSummary(event, resourceId);
  }

  protected async toggleFavorite(trigger: TelemetryTrigger, via?: string) {
    if (!this.workspacesService.isFeatureDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG)) {
      super.toggleFavorite(trigger);
      this.model.isFavorite = !this.model.isFavorite;
      this.cdr.markForCheck();
      const jsonData = via ? { 'action.trigger': via } : null;
      this.invoke.emit({
        command: { type: 'change-favorite-status', status: this.model.isFavorite ? 'add' : 'remove' } as Commands.ChangeFavoriteStatus,
        context: this.model.showResultSections?.hasResource
          ? { searchId: this.searchId, linkId: this.model.link.id, resource: { id: this.model.resource.id, kind: 'link-resource' } }
          : null,
        trigger,
        invokerItem: this.model,
        eventInfo: {
          label: `${this.model.isFavorite ? 'add' : 'remove'}_favorite`,
          target: trigger,
          jsonData: jsonData ?? undefined,
        },
      });
      const label = `${this.model.isFavorite ? 'add' : 'remove'}_favorite`; // inverse since we already toggled
      this.sendEvent({
        label,
        location: { title: this.getLocation() },
        resources: this.getEventResources(),
      });
    }
  }
}
